import { mdiChevronDown, mdiChevronUp, mdiMagnify, mdiTune } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState } from 'react';
import {
    FILE_CABINET_FTS_EMPTY,
    FILE_CABINET_FTS_REFERENCE,
    FileCabinetFtsModel,
    fileCabinetFtsModel,
    fileCabinetFtsQuery,
} from '../../services/file-cabinet/file-cabinet.fts';
import { searchValue } from '../../services/file-cabinet/file-cabinet.query';
import type { FileCabinetSearch } from '../../services/file-cabinet/file-cabinet.views';
import { BaseButton } from '../base/button/button';
import { BaseInput } from '../base/input/input';
import { Popover } from '../base/popover/popover';
import * as S from './file-cabinet.styles';

/**
 * The full-text query builder (design: `FtsSearch.jsx`): fields for what must, may and must not be in
 * the text, building the boolean query the box would otherwise need typed by hand. It opens on the
 * query in the box and gives back a new one on Search.
 *
 * The design's "Ranking" section (`>word`, `~word`) is left out: those operators only reorder by
 * relevance, and these lists sort by date with no relevance option — a control with no visible effect.
 * A query that has them keeps them (they show in the preview), and the reference names them.
 */
export function FileCabinetFtsBuilder({
    search,
    query,
    onSearch,
}: {
    search: FileCabinetSearch;
    query: string;
    onSearch: (query: string) => void;
}): React.ReactElement {
    return (
        <Popover
            align="right"
            renderButton={(): React.ReactElement => (
                <BaseButton variant="outlined" size="compact" type="button" aria-haspopup="dialog">
                    <S.ButtonContent>
                        <Icon path={mdiTune} size={0.65} />
                        Builder
                    </S.ButtonContent>
                </BaseButton>
            )}
        >
            <BuilderPanel search={search} query={query} onSearch={onSearch} />
        </Popover>
    );
}

/** `closeMenu` comes from the `Popover`, which hands it to its children. */
function BuilderPanel({
    search,
    query,
    onSearch,
    closeMenu,
}: {
    search: FileCabinetSearch;
    query: string;
    onSearch: (query: string) => void;
    closeMenu?: () => void;
}): React.ReactElement {
    // Mounted each time the popover opens: the fields start from the query in the box.
    const [model, setModel] = useState<FileCabinetFtsModel>(() => fileCabinetFtsModel(query));
    const [reference, setReference] = useState(false);
    const built = fileCabinetFtsQuery(model);
    // What would be sent — the same normalising as the box (a comma never reaches the wire).
    const sent = searchValue(search.mode, built);
    const set =
        (key: keyof FileCabinetFtsModel) =>
        (e: React.ChangeEvent<HTMLInputElement>): void =>
            setModel(current => ({ ...current, [key]: e.target.value }));
    const submit = (): void => {
        onSearch(built);
        closeMenu?.();
    };
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            submit();
        }
    };

    const row = (key: keyof FileCabinetFtsModel, label: string, operator: string, placeholder: string): React.ReactElement => (
        <>
            <S.FtsLabel htmlFor={`fts-${key}`}>{label}</S.FtsLabel>
            <BaseInput
                id={`fts-${key}`}
                compact={true}
                value={String(model[key])}
                placeholder={placeholder}
                disableLatin1Validation={true}
                onChange={set(key)}
                onKeyDown={onKeyDown}
            />
            <S.FtsOperator>{operator}</S.FtsOperator>
        </>
    );

    return (
        // The query can hold a patient's name: blocked from session replay.
        <S.FtsPanel role="dialog" aria-label="Full-text search builder" data-sentry-block>
            <S.FtsPanelTitle>
                Full-text search
                <S.Chip tone="primary">{search.tag}</S.Chip>
            </S.FtsPanelTitle>
            <S.FtsGrid>
                {row('all', 'All of these', '+word', 'denial cigna')}
                {row('any', 'At least one of', '+(a b)', 'denied adverse')}
                {row('phrase', 'Exact phrase', '+"…"', 'prior authorization')}
                <S.FtsLabel htmlFor="fts-near">Near each other</S.FtsLabel>
                <S.FtsNear>
                    <BaseInput
                        id="fts-near"
                        compact={true}
                        value={model.near}
                        placeholder="records denied"
                        disableLatin1Validation={true}
                        onChange={set('near')}
                        onKeyDown={onKeyDown}
                    />
                    within
                    <S.FtsDistance>
                        <BaseInput
                            type="number"
                            min={1}
                            max={50}
                            compact={true}
                            aria-label="Words apart, at most"
                            value={model.distance}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                                setModel(current => ({ ...current, distance: Math.max(1, Number(e.target.value) || 1) }))
                            }
                            onKeyDown={onKeyDown}
                        />
                    </S.FtsDistance>
                    words
                </S.FtsNear>
                <S.FtsOperator>"a b" @N</S.FtsOperator>
                {row('not', 'None of these', '-word', 'approved "paid in full"')}
                {row('extra', 'Advanced', 'as typed', '+(request requested) "a b" @4')}
            </S.FtsGrid>
            <S.FtsHint>
                End a word with <S.Mono>*</S.Mono> to match its start (<S.Mono>auth*</S.Mono>). Very short and very common words are
                usually not indexed, so they may be ignored.
            </S.FtsHint>
            <S.FtsPreview>
                <S.FtsPreviewLabel>Query</S.FtsPreviewLabel>
                <S.Mono>{sent || 'Empty'}</S.Mono>
                {sent && (
                    <S.FtsWire>
                        {search.filterId}=FTS:{encodeURIComponent(sent)}
                    </S.FtsWire>
                )}
            </S.FtsPreview>
            <div>
                <BaseButton variant="text" size="small" type="button" aria-expanded={reference} onClick={(): void => setReference(r => !r)}>
                    <S.ButtonContent>
                        <Icon path={reference ? mdiChevronUp : mdiChevronDown} size={0.65} />
                        Syntax reference
                    </S.ButtonContent>
                </BaseButton>
                {reference && (
                    <S.FtsReference>
                        {FILE_CABINET_FTS_REFERENCE.map(([syntax, meaning]) => (
                            <React.Fragment key={syntax}>
                                <dt>{syntax}</dt>
                                <dd>{meaning}</dd>
                            </React.Fragment>
                        ))}
                    </S.FtsReference>
                )}
            </div>
            <S.FtsFooter>
                <BaseButton variant="outlined" size="compact" type="button" onClick={(): void => setModel(FILE_CABINET_FTS_EMPTY)}>
                    Clear
                </BaseButton>
                <BaseButton variant="primary" size="compact" type="button" onClick={submit}>
                    <S.ButtonContent>
                        <Icon path={mdiMagnify} size={0.65} />
                        Search
                    </S.ButtonContent>
                </BaseButton>
            </S.FtsFooter>
        </S.FtsPanel>
    );
}
